import React, { useState, useEffect, useMemo } from 'react';
import './allOrders.css';

const MARK_ORDER_READ_ENDPOINT = 'https://car.amar.co.jp/api/authorization/calculate-order-history/mark-order-read';

const AllOrders = ({ autoOpenId, onAutoOpenConsumed, onNavigateToInquiry, onNavigateToInvoice }) => {
  const [filter, setFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const pageSize = 20;

  const getStatusClassName = (status) => {
    if (!status || status === 'N/A') return 'ao-status-default';
    return 'ao-status-' + status.toLowerCase().replace(/[_\s]+/g, '-');
  };

  const getStatusDisplayText = (status) => {
    if (!status || status === 'N/A') return 'N/A';
    return status.replace(/_/g, ' ');
  };

  // Fetch orders with pagination
  const fetchOrders = async (page = 0) => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://car.amar.co.jp/api/authorization/admin-all-orders/placed-orders?page=${page}&size=${pageSize}`,
        {
          headers: {
            'accept': 'application/json',
          }
        }
      );

      if (!response.ok) throw new Error(`Failed to fetch orders: ${response.statusText}`);

      const data = await response.json();

      if (data.success && Array.isArray(data.data)) {
        const transformedOrders = data.data.map(order => ({
          id: `${order.orderRefId}`,
          orderRefId: order.orderRefId,
          isOrderRead: order.isOrderRead !== false,
          customer: `${order.firstName || ''} ${order.lastName || ''}`.trim() || 'Unknown Customer',
          car: `${order.carBrandName || ''} ${order.carModelName || ''} ${order.year || ''}`.trim(),
          date: order.dateOfOrder || 'N/A',
          amount: order.totalCost ? `$${order.totalCost.toLocaleString()}` : '$0',
          status: order.currentStatus || 'N/A',
          carDetails: {
            carImage: order.carModelId
              ? `https://car.amar.co.jp/api/image/car-exterior-images-1/get-image/${order.carModelId}`
              : 'https://via.placeholder.com/300',
            categories: order.categoriesName || 'N/A',
            brand: order.carBrandName || 'N/A',
            model: order.carModelName || 'N/A',
            price: order.carPrice ? `$${order.carPrice.toLocaleString()}` : '$0',
            year: order.year || 'N/A',
            car_ref_number: order.carRefNumber ? `REF-${order.carRefNumber}` : 'N/A',
            car_registration_date: order.carRegistrationDate || 'N/A',
            car_manufacture_date: order.carManufactureDate || 'N/A',
            car_model_code: order.carModelCode || 'N/A',
            car_grade: order.carGrade || 'N/A',
            car_chassis_number: order.carChassisNumber || 'N/A',
            car_engine_cc: order.carEngineCC || 'N/A',
            car_engine_code: order.carEngineCode || 'N/A',
            car_fuel: order.carFuel || 'N/A',
            car_transmission: order.carTransmission || 'N/A',
            car_steering: order.carSteering || 'N/A',
            car_seat: order.carSeat || 'N/A',
            car_doors: order.carDoor || 'N/A',
            car_mileage: order.carMileage ? `${order.carMileage} miles` : 'N/A',
            car_color: order.carColor || 'N/A',
            car_size: order.carSize || 'N/A',
            car_m3: order.carM3 || 'N/A',
            car_drive: order.carDrive || 'N/A',
            car_location: order.carLocation || 'N/A',
            car_length: order.carLength ? `${order.carLength} m` : 'N/A',
            car_width: order.carWidth ? `${order.carWidth} m` : 'N/A',
            car_height: order.carHeight ? `${order.carHeight} m` : 'N/A',
            our_pick: order.ourPick || false,
            special_deal: order.specialDeal || false,
            air_conditioning: order.airConditioning === 'ACC',
            total_base_shipping_cost_all: order.totalBaseShippingCostAll
              ? `$${order.totalBaseShippingCostAll.toLocaleString()}` : 'N/A',
            total_cost_all_cars: order.totalCostAllCars
              ? `$${order.totalCostAllCars.toLocaleString()}` : 'N/A',
            total_cost: order.totalCost
              ? `$${order.totalCost.toLocaleString()}` : 'N/A',
            car_price: order.carPrice
              ? `$${order.carPrice.toLocaleString()}` : 'N/A',
            shipping_option: order.shippingOption || 'N/A',
            country_name: order.countryName || 'N/A',
            port_name: order.portName || 'N/A',
            volumeCubicMeters: order.volumeCubicMeters,
            userId: order.userId,
            userEmailId: order.userEmailId,
            // Inquiry-time number first (backend to add guestMobileNumber), profile number as fallback
            userMobileNumber: order.guestMobileNumber || order.userMobileNumber,
            gender: order.gender,
            dateOfBirth: order.dateOfBirth,
            inquiryId: order.inquiryId || 'N/A',
            proformaInvoiceId: order.proformaInvoiceId || 'N/A',
            orderRefId: order.orderRefId || 'N/A'
          }
        }));

        setOrders(transformedOrders);
        setTotalCount(data.totalCount || transformedOrders.length);
        setTotalPages(data.totalPages || 0);
        setCurrentPage(page);
      } else {
        throw new Error('Invalid API response format');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchOrders(0);
  }, []);

  // Auto-open order
  useEffect(() => {
    if (autoOpenId && orders.length > 0 && !loading) {
      const order = orders.find(
        o => o.id === autoOpenId ||
          o.id === String(autoOpenId) ||
          o.carDetails?.orderRefId === autoOpenId ||
          o.carDetails?.orderRefId === String(autoOpenId)
      );
      if (order) {
        openModal(order);
        if (onAutoOpenConsumed) onAutoOpenConsumed();
      }
    }
  }, [autoOpenId, orders, loading]);

  const handlePageChange = (page) => {
    if (page < 0 || page >= totalPages || page === currentPage) return;
    fetchOrders(page);
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible + 2) {
      for (let i = 0; i < totalPages; i++) pages.push(i);
    } else {
      pages.push(0);
      let start = Math.max(1, currentPage - 1);
      let end = Math.min(totalPages - 2, currentPage + 1);

      if (currentPage <= 2) { start = 1; end = maxVisible - 2; }
      else if (currentPage >= totalPages - 3) { start = totalPages - maxVisible + 1; end = totalPages - 2; }

      if (start > 1) pages.push('...');
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < totalPages - 2) pages.push('...');
      pages.push(totalPages - 1);
    }
    return pages;
  };

  const markOrderAsRead = async (orderRefId) => {
    // Optimistically update UI so highlight disappears immediately
    setOrders(prev =>
      prev.map(o => (o.orderRefId === orderRefId ? { ...o, isOrderRead: true } : o))
    );
    setSelectedOrder(prev =>
      prev && prev.orderRefId === orderRefId ? { ...prev, isOrderRead: true } : prev
    );

    try {
      const response = await fetch(`${MARK_ORDER_READ_ENDPOINT}/${orderRefId}`, {
        method: 'PUT',
        headers: { 'accept': 'application/json' },
      });
      if (!response.ok) throw new Error('Failed to mark order as read');
    } catch (err) {
      console.error('Error marking order as read:', err);
      // Revert optimistic update on failure
      setOrders(prev =>
        prev.map(o => (o.orderRefId === orderRefId ? { ...o, isOrderRead: false } : o))
      );
    }
  };

  const openModal = (order) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
    if (!order.isOrderRead) {
      markOrderAsRead(order.orderRefId);
    }
  };
  const closeModal = () => { setIsModalOpen(false); };

  const unreadCount = useMemo(
    () => orders.filter(o => !o.isOrderRead).length,
    [orders]
  );

  const filteredOrders = filter === 'all'
    ? orders
    : orders.filter(order => order.status.toLowerCase().includes(filter.toLowerCase()));

  // Column sorting (click a header) — matches the pattern used in
  // AllTickets.js / AllUsers.js / AllAdmins.js.
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <span style={{ marginLeft: '4px', fontSize: '10px', color: 'white' }}>▲▼</span>;
    return <span style={{ marginLeft: '4px', fontSize: '11px', color: 'white' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>;
  };

  // date comes as "DD-MM-YYYY" — not natively parseable by `new Date(...)`.
  const parseOrderDate = (dateStr) => {
    if (!dateStr || dateStr === 'N/A') return 0;
    const [dd, mm, yyyy] = dateStr.split('-').map(Number);
    if (!dd || !mm || !yyyy) return 0;
    return new Date(yyyy, mm - 1, dd).getTime();
  };

  const sortedFilteredOrders = !sortField ? filteredOrders : [...filteredOrders].sort((a, b) => {
    if (sortField === 'date') {
      return sortDirection === 'asc'
        ? parseOrderDate(a.date) - parseOrderDate(b.date)
        : parseOrderDate(b.date) - parseOrderDate(a.date);
    }
    if (sortField === 'amount') {
      const aN = parseFloat((a.amount || '0').replace(/[^0-9.-]/g, '')) || 0;
      const bN = parseFloat((b.amount || '0').replace(/[^0-9.-]/g, '')) || 0;
      return sortDirection === 'asc' ? aN - bN : bN - aN;
    }
    const fieldMap = {
      id: (x) => x.id,
      customer: (x) => x.customer,
      car: (x) => x.car,
      status: (x) => x.status,
    };
    const aV = (fieldMap[sortField]?.(a) || '').toString().toLowerCase();
    const bV = (fieldMap[sortField]?.(b) || '').toString().toLowerCase();
    if (aV < bV) return sortDirection === 'asc' ? -1 : 1;
    if (aV > bV) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  if (error) {
    return (
      <div className="ao-container">
        <div className="ao-error-message">
          <p>Error: {error}</p>
          <button onClick={() => { setError(null); fetchOrders(0); }}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="ao-container">
      <div className="ao-header" style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
        <h2 style={{ margin: '0 0 20px 0', fontSize: '1.4rem', fontWeight: 700, color: '#0f172a' }}>
          All Orders ({totalCount})
          <span style={{ display: 'block', width: 36, height: 3, background: '#e11d48', borderRadius: 2, marginTop: 6 }}></span>
        </h2>
        {unreadCount > 0 && (
          <div
            className="ao-unread-badge"
            style={{
              background: '#dc2626', color: '#fff', fontSize: '13px', fontWeight: 700,
              padding: '4px 12px', borderRadius: '999px'
            }}
          >
            {unreadCount} New Order{unreadCount > 1 ? 's' : ''}
          </div>
        )}
      </div>

      <div className="ao-table-container">
        {loading ? (
          <div className="ao-loading">Loading orders...</div>
        ) : (
          <>
            <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '70vh' }}>
            <table className="ao-table">
              <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr style={{ backgroundColor: '#d32f2f' }}>
                  <th onClick={() => handleSort('id')} style={{ backgroundColor: '#d32f2f', color: 'white', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>Order ID{getSortIcon('id')}</th>
                  <th onClick={() => handleSort('customer')} style={{ backgroundColor: '#d32f2f', color: 'white', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>Customer{getSortIcon('customer')}</th>
                  <th onClick={() => handleSort('car')} style={{ backgroundColor: '#d32f2f', color: 'white', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>Car{getSortIcon('car')}</th>
                  <th onClick={() => handleSort('date')} style={{ backgroundColor: '#d32f2f', color: 'white', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', textAlign: 'center' }}>Date{getSortIcon('date')}</th>
                  <th onClick={() => handleSort('amount')} style={{ backgroundColor: '#d32f2f', color: 'white', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', textAlign: 'center' }}>Amount{getSortIcon('amount')}</th>
                  <th onClick={() => handleSort('status')} style={{ backgroundColor: '#d32f2f', color: 'white', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', textAlign: 'center' }}>Status{getSortIcon('status')}</th>
                  <th style={{ backgroundColor: '#d32f2f', color: 'white', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedFilteredOrders.length === 0 ? (
                  <tr><td colSpan="7" className="ao-no-data">No orders found</td></tr>
                ) : (
                  sortedFilteredOrders.map((order, index) => (
                    <tr
                      key={index}
                      className={!order.isOrderRead ? 'ao-unread' : ''}
                      style={!order.isOrderRead ? { background: 'rgba(220, 38, 38, 0.08)', borderLeft: '3px solid #dc2626' } : undefined}
                    >
                      <td>{order.id}</td>
                      <td>
                        {order.customer}
                        {!order.isOrderRead && (
                          <span
                            className="ao-unread-dot"
                            style={{
                              display: 'inline-block', width: '8px', height: '8px',
                              borderRadius: '50%', background: '#dc2626', marginLeft: '8px'
                            }}
                          ></span>
                        )}
                      </td>
                      <td>{order.car}</td>
                      <td style={{ textAlign: 'center' }}>{order.date}</td>
                      <td style={{ textAlign: 'center' }}>{order.amount}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`ao-status-badge ${getStatusClassName(order.status)}`}>
                          {getStatusDisplayText(order.status)}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button className="ao-action-btn ao-view-btn" onClick={() => openModal(order)}>
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="ao-pagination">
                <button
                  className="ao-page-btn"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 0}
                >
                  ← Prev
                </button>

                {getPageNumbers().map((page, idx) =>
                  page === '...' ? (
                    <span key={`ellipsis-${idx}`} className="ao-page-ellipsis">...</span>
                  ) : (
                    <button
                      key={page}
                      className={`ao-page-btn ${currentPage === page ? 'ao-page-active' : ''}`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page + 1}
                    </button>
                  )
                )}

                <button
                  className="ao-page-btn"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages - 1}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Order Details Modal */}
      {isModalOpen && selectedOrder && (
        <div className="ao-modal-overlay">
          <div className="ao-modal">
            <div className="ao-modal-header">
              <h3>Order Details - {selectedOrder.id}</h3>
              <button className="ao-modal-close-x" onClick={closeModal}>×</button>
            </div>

            <div className="ao-modal-content">
              <div className="ao-car-image-section">
                <img
                  src={selectedOrder.carDetails.carImage}
                  alt={`${selectedOrder.carDetails.brand} ${selectedOrder.carDetails.model}`}
                  className="ao-car-image"
                />
                <div className="ao-car-basic-info">
                  <h4>{selectedOrder.carDetails.brand} {selectedOrder.carDetails.model} {selectedOrder.carDetails.year}</h4>
                  <div className="ao-price-badge">{selectedOrder.carDetails.price}</div>
                  <div className="ao-car-meta">
                    <span>{selectedOrder.carDetails.car_fuel}</span>
                    <span>{selectedOrder.carDetails.car_transmission}</span>
                    <span>{selectedOrder.carDetails.car_mileage}</span>
                  </div>
                </div>
              </div>

              <div className="ao-details-sections">
                <div className="ao-details-section">
                  <h4 className="ao-section-title">Reference Numbers</h4>
                  <div className="ao-specs-grid">
                    <div className="ao-spec-item">
                      <span className="ao-spec-label">Inquiry Number</span>
                      {selectedOrder.carDetails.inquiryId && selectedOrder.carDetails.inquiryId !== 'N/A' ? (
                        <span
                          className="ao-spec-value clickable-ref"
                          onClick={() => { closeModal(); onNavigateToInquiry && onNavigateToInquiry(selectedOrder.carDetails.inquiryId); }}
                          title="View Inquiry"
                        >
                          {selectedOrder.carDetails.inquiryId}
                          <svg width="14" height="14" viewBox="0 0 14 14" style={{ marginLeft: '6px', verticalAlign: 'middle' }}>
                            <path d="M5.5 2L10 7L5.5 12" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                      ) : (
                        <span className="ao-spec-value">N/A</span>
                      )}
                    </div>
                    <div className="ao-spec-item">
                      <span className="ao-spec-label">Proforma Invoice Number</span>
                      {selectedOrder.carDetails.proformaInvoiceId && selectedOrder.carDetails.proformaInvoiceId !== 'N/A' ? (
                        <span
                          className="ao-spec-value clickable-ref"
                          onClick={() => { closeModal(); onNavigateToInvoice && onNavigateToInvoice(selectedOrder.carDetails.proformaInvoiceId); }}
                          title="View Proforma Invoice"
                        >
                          {selectedOrder.carDetails.proformaInvoiceId}
                          <svg width="14" height="14" viewBox="0 0 14 14" style={{ marginLeft: '6px', verticalAlign: 'middle' }}>
                            <path d="M5.5 2L10 7L5.5 12" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                      ) : (
                        <span className="ao-spec-value">N/A</span>
                      )}
                    </div>
                    <div className="ao-spec-item">
                      <span className="ao-spec-label">Order Invoice Number</span>
                      <span className="ao-spec-value">{selectedOrder.carDetails.orderRefId}</span>
                    </div>
                  </div>
                </div>

                <div className="ao-details-section">
                  <h4 className="ao-section-title">Customer Information</h4>
                  <div className="ao-specs-grid">
                    <div className="ao-spec-item">
                      <span className="ao-spec-label">Name</span>
                      <span className="ao-spec-value">{selectedOrder.customer}</span>
                    </div>
                    <div className="ao-spec-item">
                      <span className="ao-spec-label">Email</span>
                      <span className="ao-spec-value">{selectedOrder.carDetails.userEmailId || 'N/A'}</span>
                    </div>
                    <div className="ao-spec-item">
                      <span className="ao-spec-label">Phone</span>
                      <span className="ao-spec-value">{selectedOrder.carDetails.userMobileNumber || 'N/A'}</span>
                    </div>
                    <div className="ao-spec-item">
                      <span className="ao-spec-label">Gender</span>
                      <span className="ao-spec-value">{selectedOrder.carDetails.gender || 'N/A'}</span>
                    </div>
                    <div className="ao-spec-item">
                      <span className="ao-spec-label">Date of Birth</span>
                      <span className="ao-spec-value">{selectedOrder.carDetails.dateOfBirth || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                <div className="ao-details-section">
                  <h4 className="ao-section-title">Order Status</h4>
                  <div className="ao-status-payment-container">
                    <div className="ao-status-display">
                      <span className="ao-status-label-text">Status:</span>
                      <span className={`ao-status-badge ${getStatusClassName(selectedOrder.status)}`}>
                        {getStatusDisplayText(selectedOrder.status)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="ao-details-section">
                  <h4 className="ao-section-title">Car Specifications</h4>
                  <div className="ao-specs-grid">
                    <div className="ao-spec-item"><span className="ao-spec-label">Reference Number</span><span className="ao-spec-value">{selectedOrder.carDetails.car_ref_number}</span></div>
                    <div className="ao-spec-item"><span className="ao-spec-label">Model Code</span><span className="ao-spec-value">{selectedOrder.carDetails.car_model_code}</span></div>
                    <div className="ao-spec-item"><span className="ao-spec-label">Chassis Number</span><span className="ao-spec-value">{selectedOrder.carDetails.car_chassis_number}</span></div>
                    <div className="ao-spec-item"><span className="ao-spec-label">Engine CC</span><span className="ao-spec-value">{selectedOrder.carDetails.car_engine_cc}</span></div>
                    <div className="ao-spec-item"><span className="ao-spec-label">Engine Code</span><span className="ao-spec-value">{selectedOrder.carDetails.car_engine_code}</span></div>
                    <div className="ao-spec-item"><span className="ao-spec-label">Fuel Type</span><span className="ao-spec-value">{selectedOrder.carDetails.car_fuel}</span></div>
                    <div className="ao-spec-item"><span className="ao-spec-label">Transmission</span><span className="ao-spec-value">{selectedOrder.carDetails.car_transmission}</span></div>
                    <div className="ao-spec-item"><span className="ao-spec-label">Steering</span><span className="ao-spec-value">{selectedOrder.carDetails.car_steering}</span></div>
                    <div className="ao-spec-item"><span className="ao-spec-label">Seats</span><span className="ao-spec-value">{selectedOrder.carDetails.car_seat}</span></div>
                    <div className="ao-spec-item"><span className="ao-spec-label">Doors</span><span className="ao-spec-value">{selectedOrder.carDetails.car_doors}</span></div>
                    <div className="ao-spec-item"><span className="ao-spec-label">Color</span><span className="ao-spec-value">{selectedOrder.carDetails.car_color}</span></div>
                    <div className="ao-spec-item"><span className="ao-spec-label">Drive</span><span className="ao-spec-value">{selectedOrder.carDetails.car_drive}</span></div>
                  </div>
                </div>

                <div className="ao-details-section">
                  <h4 className="ao-section-title">Dimensions</h4>
                  <div className="ao-specs-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                    <div className="ao-spec-item"><span className="ao-spec-label">Length</span><span className="ao-spec-value">{selectedOrder.carDetails.car_length}</span></div>
                    <div className="ao-spec-item"><span className="ao-spec-label">Width</span><span className="ao-spec-value">{selectedOrder.carDetails.car_width}</span></div>
                    <div className="ao-spec-item"><span className="ao-spec-label">Height</span><span className="ao-spec-value">{selectedOrder.carDetails.car_height}</span></div>
                  </div>
                </div>

                <div className="ao-details-section">
                  <h4 className="ao-section-title">Shipping Information</h4>
                  <div className="ao-specs-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                    <div className="ao-spec-item"><span className="ao-spec-label">Shipping Option</span><span className="ao-spec-value">{selectedOrder.carDetails.shipping_option}</span></div>
                    <div className="ao-spec-item"><span className="ao-spec-label">Country</span><span className="ao-spec-value">{selectedOrder.carDetails.country_name}</span></div>
                    <div className="ao-spec-item"><span className="ao-spec-label">Port</span><span className="ao-spec-value">{selectedOrder.carDetails.port_name}</span></div>
                  </div>
                  <div className="ao-specs-grid" style={{ marginTop: '14px', gridTemplateColumns: 'repeat(3, 1fr)' }}>
                    <div className="ao-spec-item"><span className="ao-spec-label">Car Price</span><span className="ao-spec-value">{selectedOrder.carDetails.car_price}</span></div>
                    <div className="ao-spec-item"><span className="ao-spec-label">Shipping Cost</span><span className="ao-spec-value">{selectedOrder.carDetails.total_base_shipping_cost_all}</span></div>
                    <div className="ao-spec-item"><span className="ao-spec-label">Total Cost</span><span className="ao-spec-value">{selectedOrder.carDetails.total_cost}</span></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="ao-modal-footer">
              <button className="ao-modal-close-btn" onClick={closeModal}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllOrders;